import { Content, DefaultPage } from "@shared/layout/DefaultPage";
import { Heading } from "@ui/Heading";
import { ROUTES } from "@shared/constants/routes";
import { ProfileAvatar } from "@ui/ProfileAvatar";
import { nameSchema, surnameSchema, useUserService } from "@entities/User";
import { useEffect, useRef, useState } from "react";
import { useUploadImage } from "@features/profile/uploadImage";
import { toast } from "sonner";
import { observer } from "mobx-react-lite";
import SkeletonCard from "react-loading-skeleton";
import { useIonRouter } from "@ionic/react";
import { BackArrowHeader } from "@widgets/BackArrowHeader";
import {
  CalendarGlyph,
  DumbbellGlyph,
  PencilGlyph,
  TrophyGlyph,
  UserGlyph,
} from "../ui/profile-glyphs";
import {
  clampDay,
  DateParts,
  DateWheelPicker,
  formatDisplayDate,
  GenderSwitchCard,
  InfoBottomSheet,
  InfoField,
  normalizeDateParts,
  OptionListPicker,
  ProfileInfoForm,
  TextSheetInput,
  toIsoDate,
} from "@shared/ui/ProfileInfoForm";
import "./styles.css";

type ProfileEditableField =
  | "firstName"
  | "lastName"
  | "birthday"
  | "favoriteSport"
  | "yearsExperience";

const EXPERIENCE_OPTIONS = [
  { value: "0", label: "Нет опыта" },
  { value: "1", label: "1 год" },
  { value: "2", label: "2 года" },
  { value: "3", label: "3 года" },
  { value: "4", label: "4+ года" },
];

const getFieldTitle = (field: ProfileEditableField): string => {
  const titles: Record<ProfileEditableField, string> = {
    firstName: "Введите имя",
    lastName: "Введите фамилию",
    birthday: "Выберите дату рождения",
    favoriteSport: "Любимый спорт",
    yearsExperience: "Опыт тренировок",
  };

  return titles[field];
};

const getExperienceLabel = (value: string): string => {
  return (
    EXPERIENCE_OPTIONS.find((option) => option.value === value)?.label || value
  );
};

export const ProfileEditPage = observer(() => {
  const userService = useUserService();
  const nav = useIonRouter();
  if (!userService) {
    return null;
  }
  const { data: userData } = userService;
  const {
    uploadAvatar,
    status: uploadStatus,
    error: uploadError,
    newAvatar,
  } = useUploadImage();
  const [avatarUrl, setAvatarUrl] = useState<string | undefined>(undefined);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [activeField, setActiveField] = useState<ProfileEditableField | null>(
    null,
  );
  const [draftValue, setDraftValue] = useState<string>("");
  const [dateDraft, setDateDraft] = useState<DateParts>(normalizeDateParts(""));
  const [formData, setFormData] = useState({
    firstName: userData.firstName || "",
    lastName: userData.lastName || "",
    birthday: userData.birthday || "",
    sex: userData.sex || "",
    yearsExperience:
      userData.yearsExperience === undefined
        ? ""
        : String(userData.yearsExperience),
    favoriteSport: userData.favoriteSport || "",
  });

  useEffect(() => {
    setFormData({
      firstName: userService.data.firstName || "",
      lastName: userService.data.lastName || "",
      birthday: userService.data.birthday || "",
      sex: userService.data.sex || "",
      yearsExperience:
        userService.data.yearsExperience === undefined
          ? ""
          : String(userService.data.yearsExperience),
      favoriteSport: userService.data.favoriteSport || "",
    });
  }, [userService.data]);

  useEffect(() => {
    setAvatarUrl(userService.avatarUrl);
  }, [userService.avatarUrl]);

  const openEditor = (field: ProfileEditableField) => {
    setActiveField(field);

    if (field === "firstName") {
      setDraftValue(formData.firstName);
    }

    if (field === "lastName") {
      setDraftValue(formData.lastName);
    }

    if (field === "favoriteSport") {
      setDraftValue(formData.favoriteSport);
    }

    if (field === "yearsExperience") {
      setDraftValue(formData.yearsExperience);
    }

    if (field === "birthday") {
      setDateDraft(normalizeDateParts(formData.birthday));
    }
  };

  const closeEditor = () => {
    setActiveField(null);
    setDraftValue("");
  };

  const commitEditor = () => {
    if (!activeField) {
      return;
    }

    const trimmedValue = draftValue.trim();

    if (activeField === "firstName") {
      const parsed = nameSchema.safeParse(trimmedValue);
      if (!parsed.success) {
        toast.error(parsed.error.errors[0].message);
        return;
      }
      setFormData((rest) => ({ ...rest, firstName: trimmedValue }));
    }

    if (activeField === "lastName") {
      const parsed = surnameSchema.safeParse(trimmedValue);
      if (!parsed.success) {
        toast.error(parsed.error.errors[0].message);
        return;
      }
      setFormData((rest) => ({ ...rest, lastName: trimmedValue }));
    }

    if (activeField === "favoriteSport") {
      setFormData((rest) => ({ ...rest, favoriteSport: trimmedValue }));
    }

    if (activeField === "yearsExperience") {
      setFormData((rest) => ({ ...rest, yearsExperience: trimmedValue }));
    }

    if (activeField === "birthday") {
      const day = clampDay(dateDraft.day, dateDraft.month, dateDraft.year);
      setFormData((rest) => ({
        ...rest,
        birthday: toIsoDate(day, dateDraft.month, dateDraft.year),
      }));
    }

    closeEditor();
  };

  const handleSave = async () => {
    const yearsExperience =
      formData.yearsExperience === ""
        ? undefined
        : Number(formData.yearsExperience);
    const success = await userService.updateData({
      firstName: formData.firstName,
      sex: formData.sex,
      yearsExperience,
      favoriteSport: formData.favoriteSport,
      lastName: formData.lastName,
      birthday: formData.birthday,
    });

    if (success) {
      toast.success("Данные профиля сохранены!");
    }
  };

  const handleFileChange = async (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    const file = event.target.files?.[0];
    if (file) {
      const avatar = await uploadAvatar(file);
      if (avatar) {
        userService.setAvatar(avatar);
        setAvatarUrl(avatar.url);
        toast.success("Аватар успешно загружен!");
      } else {
        toast.error(
          uploadError
            ? `Ошибка загрузки: ${uploadError}`
            : "Ошибка загрузки аватара",
        );
      }
      event.target.value = "";
    }
  };

  const handleEditClick = () => {
    if (fileInputRef.current) {
      fileInputRef.current.click();
    }
  };

  const handleBack = () => {
    nav.push(ROUTES.overall.profile, "back", "push", { unmount: true });
  };

  const handleLogout = () => {
    nav.push(ROUTES.auth.logout, "forward", "replace");
  };

  const renderEditorContent = () => {
    if (activeField === "firstName" || activeField === "lastName") {
      return (
        <TextSheetInput
          placeholder={
            activeField === "firstName" ? "Введите имя" : "Введите фамилию"
          }
          value={draftValue}
          onChange={setDraftValue}
        />
      );
    }

    if (activeField === "favoriteSport") {
      return (
        <TextSheetInput
          placeholder="Например, футбол"
          value={draftValue}
          onChange={setDraftValue}
        />
      );
    }

    if (activeField === "yearsExperience") {
      return (
        <OptionListPicker
          options={EXPERIENCE_OPTIONS}
          value={draftValue}
          onChange={setDraftValue}
        />
      );
    }

    if (activeField === "birthday") {
      return <DateWheelPicker value={dateDraft} onChange={setDateDraft} />;
    }

    return null;
  };

  return (
    <DefaultPage noPadding className="auth-info-page profile-edit-page">
      <Content className="auth-info-content profile-edit-content">
        <div className="profile-edit-shell">
          <BackArrowHeader
            centeredChildren
            goBackFunction={handleBack}
            className="profile-edit-header"
            arrowClassName="profile-edit-back"
            arrowStyle={{ marginLeft: 0 }}
          >
            <Heading size={22} weight={700} centered>
              Редактирование профиля
            </Heading>
          </BackArrowHeader>
          <form
            className="profile-edit-form"
            onSubmit={(e) => e.preventDefault()}
          >
            <div className="profile-edit-avatar-block">
              <input
                type="file"
                ref={fileInputRef}
                onChange={handleFileChange}
                accept="image/*"
                style={{ display: "none" }}
                disabled={uploadStatus === "loading"}
              />

              {uploadStatus !== "loading" && (
                <ProfileAvatar
                  height={148}
                  width={148}
                  style={{ backgroundColor: "#F4F5F2" }}
                  src={newAvatar !== null ? newAvatar.url : avatarUrl}
                >
                  <button
                    type="button"
                    aria-label="Изменить фото профиля"
                    className="profile-edit-avatar-button"
                    onClick={handleEditClick}
                  >
                    <PencilGlyph className="size-5" />
                  </button>
                </ProfileAvatar>
              )}
              {uploadStatus === "loading" && (
                <SkeletonCard
                  style={{ width: 148, height: 148, borderRadius: 200 }}
                />
              )}
              {uploadStatus === "error" && (
                <p className="profile-edit-avatar-error">
                  Ошибка: {uploadError}
                </p>
              )}
            </div>

            <ProfileInfoForm>
              <InfoField
                icon={<UserGlyph />}
                label="Имя"
                value={formData.firstName}
                placeholder=""
                onClick={() => openEditor("firstName")}
                onClear={() =>
                  setFormData((rest) => ({ ...rest, firstName: "" }))
                }
              />
              <InfoField
                icon={<UserGlyph />}
                label="Фамилия"
                value={formData.lastName}
                placeholder=""
                onClick={() => openEditor("lastName")}
                onClear={() =>
                  setFormData((rest) => ({ ...rest, lastName: "" }))
                }
              />
              <InfoField
                icon={<CalendarGlyph />}
                label="Дата рождения"
                value={formatDisplayDate(formData.birthday)}
                placeholder=""
                onClick={() => openEditor("birthday")}
                onClear={() =>
                  setFormData((rest) => ({ ...rest, birthday: "" }))
                }
              />
              <InfoField
                icon={<TrophyGlyph />}
                label="Любимый спорт"
                value={formData.favoriteSport}
                placeholder=""
                onClick={() => openEditor("favoriteSport")}
                onClear={() =>
                  setFormData((rest) => ({ ...rest, favoriteSport: "" }))
                }
              />
              <InfoField
                icon={<DumbbellGlyph />}
                label="Опыт тренировок"
                value={getExperienceLabel(formData.yearsExperience)}
                placeholder=""
                onClick={() => openEditor("yearsExperience")}
                onClear={() =>
                  setFormData((rest) => ({ ...rest, yearsExperience: "" }))
                }
              />
              <GenderSwitchCard
                value={formData.sex}
                onChange={(sex) => setFormData((rest) => ({ ...rest, sex }))}
              />
            </ProfileInfoForm>

            <div className="profile-edit-actions">
              <button
                className="profile-edit-logout-button"
                type="button"
                onClick={handleLogout}
              >
                Выйти из аккаунта
              </button>
            </div>
          </form>
        </div>
        <div className="profile-edit-save-bar" slot="fixed">
          <button
            className="profile-edit-save-button"
            type="button"
            onClick={handleSave}
            disabled={uploadStatus === "loading"}
          >
            <span>Сохранить изменения</span>
          </button>
        </div>
      </Content>
      {activeField && (
        <InfoBottomSheet
          title={getFieldTitle(activeField)}
          modifier={activeField}
          onClose={closeEditor}
          onCommit={commitEditor}
        >
          {renderEditorContent()}
        </InfoBottomSheet>
      )}
    </DefaultPage>
  );
});
